export type QuickRoomSurfaceInput = {
  lengthM?: number;
  widthM?: number;
  heightM?: number;
  doorCount?: number;
  largeOpeningsAreaM2?: number;
  includeCeiling?: boolean;
};

export type QuickReadySurfaceInput = {
  workingAreaM2?: number;
};

export type QuickSingleWallSurfaceInput = {
  lengthM?: number;
  heightM?: number;
  doorCount?: number;
  largeOpeningsAreaM2?: number;
};

export type ApartmentLayoutMode = 'open' | 'standard' | 'many_rooms';

export type QuickApartmentSurfaceInput = {
  apartmentAreaM2?: number;
  heightM?: number;
  doorCount?: number;
  largeOpeningsAreaM2?: number;
  bathroomAreaM2?: number;
  bathroomCeilingOnly?: boolean;
  layoutMode?: ApartmentLayoutMode;
  includeCeiling?: boolean;
};

export type SurfaceCalculationResult = {
  mode: 'ready' | 'room' | 'apartment' | 'wall';
  lengthM: number;
  widthM: number;
  heightM: number;
  doorCount: number;
  largeOpeningsAreaM2: number;
  includeCeiling: boolean;
  perimeterM: number;
  floorAreaM2: number;
  wallGrossAreaM2: number;
  grossAreaM2: number;
  doorDeductionM2: number;
  openingsDeductionM2: number;
  largeOpeningsDeductionM2: number;
  wallNetAreaM2: number;
  ceilingAreaM2: number;
  finalWorkingAreaM2: number;
  workingAreaM2: number;
  apartmentAreaM2?: number;
  bathroomAreaM2?: number;
  bathroomWallExclusionM2?: number;
  bathroomCeilingOnly?: boolean;
  bathroomModeLabel?: string;
  layoutMode?: ApartmentLayoutMode;
  layoutLabel?: string;
  confirmationStatus?: string;
};

const DOOR_DEDUCTION_M2 = 2;
const DEFAULT_APARTMENT_HEIGHT_M = 2.65;
const APARTMENT_LAYOUT_WALL_FACTORS: Record<ApartmentLayoutMode, number> = {
  open: 0.95,
  standard: 1.15,
  many_rooms: 1.35,
};
const APARTMENT_LAYOUT_LABELS: Record<ApartmentLayoutMode, string> = {
  open: 'Open space / mało ścian',
  standard: 'Standard',
  many_rooms: 'Dużo pokoi / korytarzy',
};

function roundArea(value: number): number {
  return Math.round(Math.max(0, value) * 100) / 100;
}

function safePositive(value: number | undefined): number {
  return Number.isFinite(value) && value && value > 0 ? value : 0;
}

export function calculateQuickRoomSurface(input: QuickRoomSurfaceInput): SurfaceCalculationResult {
  const lengthM = safePositive(input.lengthM);
  const widthM = safePositive(input.widthM);
  const heightM = safePositive(input.heightM);
  const doorCount = safePositive(input.doorCount);
  const largeOpeningsAreaM2 = safePositive(input.largeOpeningsAreaM2);
  const includeCeiling = input.includeCeiling ?? true;
  const perimeterM = 2 * (lengthM + widthM);
  const floorAreaM2 = lengthM * widthM;
  const wallGrossAreaM2 = perimeterM * heightM;
  const doorDeductionM2 = doorCount * DOOR_DEDUCTION_M2;
  const wallNetAreaM2 = Math.max(0, wallGrossAreaM2 - doorDeductionM2 - largeOpeningsAreaM2);
  const ceilingAreaM2 = includeCeiling ? floorAreaM2 : 0;
  const finalWorkingAreaM2 = Math.max(0, wallNetAreaM2 + ceilingAreaM2);

  return {
    mode: 'room',
    lengthM: roundArea(lengthM),
    widthM: roundArea(widthM),
    heightM: roundArea(heightM),
    doorCount: roundArea(doorCount),
    largeOpeningsAreaM2: roundArea(largeOpeningsAreaM2),
    includeCeiling,
    perimeterM: roundArea(perimeterM),
    floorAreaM2: roundArea(floorAreaM2),
    wallGrossAreaM2: roundArea(wallGrossAreaM2),
    grossAreaM2: roundArea(wallGrossAreaM2),
    doorDeductionM2: roundArea(doorDeductionM2),
    openingsDeductionM2: roundArea(largeOpeningsAreaM2),
    largeOpeningsDeductionM2: roundArea(largeOpeningsAreaM2),
    wallNetAreaM2: roundArea(wallNetAreaM2),
    ceilingAreaM2: roundArea(ceilingAreaM2),
    finalWorkingAreaM2: roundArea(finalWorkingAreaM2),
    workingAreaM2: roundArea(finalWorkingAreaM2),
  };
}

export function calculateQuickReadySurface(input: QuickReadySurfaceInput): SurfaceCalculationResult {
  const workingAreaM2 = roundArea(safePositive(input.workingAreaM2));

  return {
    mode: 'ready',
    lengthM: 0,
    widthM: 0,
    heightM: 0,
    doorCount: 0,
    largeOpeningsAreaM2: 0,
    includeCeiling: false,
    perimeterM: 0,
    floorAreaM2: 0,
    wallGrossAreaM2: workingAreaM2,
    grossAreaM2: workingAreaM2,
    doorDeductionM2: 0,
    openingsDeductionM2: 0,
    largeOpeningsDeductionM2: 0,
    wallNetAreaM2: workingAreaM2,
    ceilingAreaM2: 0,
    finalWorkingAreaM2: workingAreaM2,
    workingAreaM2,
    confirmationStatus: 'powierzchnia zatwierdzona przez wykonawcę',
  };
}

export function calculateQuickSingleWallSurface(
  input: QuickSingleWallSurfaceInput
): SurfaceCalculationResult {
  const lengthM = safePositive(input.lengthM);
  const heightM = safePositive(input.heightM);
  const doorCount = safePositive(input.doorCount);
  const largeOpeningsAreaM2 = safePositive(input.largeOpeningsAreaM2);
  const wallGrossAreaM2 = lengthM * heightM;
  const doorDeductionM2 = doorCount * DOOR_DEDUCTION_M2;
  const wallNetAreaM2 = Math.max(
    0,
    wallGrossAreaM2 - doorDeductionM2 - largeOpeningsAreaM2
  );

  return {
    mode: 'wall',
    lengthM: roundArea(lengthM),
    widthM: 0,
    heightM: roundArea(heightM),
    doorCount: roundArea(doorCount),
    largeOpeningsAreaM2: roundArea(largeOpeningsAreaM2),
    includeCeiling: false,
    perimeterM: 0,
    floorAreaM2: 0,
    wallGrossAreaM2: roundArea(wallGrossAreaM2),
    grossAreaM2: roundArea(wallGrossAreaM2),
    doorDeductionM2: roundArea(doorDeductionM2),
    openingsDeductionM2: roundArea(largeOpeningsAreaM2),
    largeOpeningsDeductionM2: roundArea(largeOpeningsAreaM2),
    wallNetAreaM2: roundArea(wallNetAreaM2),
    ceilingAreaM2: 0,
    finalWorkingAreaM2: roundArea(wallNetAreaM2),
    workingAreaM2: roundArea(wallNetAreaM2),
    confirmationStatus: 'wymiary zatwierdzone przez wykonawcę',
  };
}

export function calculateQuickApartmentSurface(input: QuickApartmentSurfaceInput): SurfaceCalculationResult {
  const apartmentAreaM2 = safePositive(input.apartmentAreaM2);
  const heightM = safePositive(input.heightM) || DEFAULT_APARTMENT_HEIGHT_M;
  const doorCount = safePositive(input.doorCount);
  const largeOpeningsAreaM2 = safePositive(input.largeOpeningsAreaM2);
  const bathroomAreaM2 = safePositive(input.bathroomAreaM2);
  const bathroomCeilingOnly = Boolean(input.bathroomCeilingOnly && bathroomAreaM2 > 0);
  const includeCeiling = input.includeCeiling ?? true;
  const layoutMode = input.layoutMode ?? 'standard';
  const layoutFactor = APARTMENT_LAYOUT_WALL_FACTORS[layoutMode] ?? APARTMENT_LAYOUT_WALL_FACTORS.standard;
  const wallGrossAreaM2 = apartmentAreaM2 * heightM * layoutFactor;
  const bathroomWallExclusionM2 = bathroomCeilingOnly ? bathroomAreaM2 * heightM * layoutFactor : 0;
  const doorDeductionM2 = doorCount * DOOR_DEDUCTION_M2;
  const wallNetAreaM2 = Math.max(
    0,
    wallGrossAreaM2 - bathroomWallExclusionM2 - doorDeductionM2 - largeOpeningsAreaM2
  );
  const ceilingAreaM2 = includeCeiling ? apartmentAreaM2 : 0;
  const finalWorkingAreaM2 = Math.max(0, wallNetAreaM2 + ceilingAreaM2);

  return {
    mode: 'apartment',
    lengthM: 0,
    widthM: 0,
    heightM: roundArea(heightM),
    doorCount: roundArea(doorCount),
    largeOpeningsAreaM2: roundArea(largeOpeningsAreaM2),
    includeCeiling,
    perimeterM: 0,
    floorAreaM2: roundArea(apartmentAreaM2),
    wallGrossAreaM2: roundArea(wallGrossAreaM2),
    grossAreaM2: roundArea(wallGrossAreaM2),
    doorDeductionM2: roundArea(doorDeductionM2),
    openingsDeductionM2: roundArea(largeOpeningsAreaM2),
    largeOpeningsDeductionM2: roundArea(largeOpeningsAreaM2),
    wallNetAreaM2: roundArea(wallNetAreaM2),
    ceilingAreaM2: roundArea(ceilingAreaM2),
    finalWorkingAreaM2: roundArea(finalWorkingAreaM2),
    workingAreaM2: roundArea(finalWorkingAreaM2),
    apartmentAreaM2: roundArea(apartmentAreaM2),
    bathroomAreaM2: roundArea(bathroomAreaM2),
    bathroomWallExclusionM2: roundArea(bathroomWallExclusionM2),
    bathroomCeilingOnly,
    bathroomModeLabel: bathroomAreaM2 <= 0
      ? 'brak'
      : bathroomCeilingOnly
        ? 'ściany wyłączone, sufit w zakresie'
        : 'całość w zakresie',
    layoutMode,
    layoutLabel: APARTMENT_LAYOUT_LABELS[layoutMode],
    confirmationStatus: 'do potwierdzenia przez wykonawcę',
  };
}
